import { nextTick } from 'vue'

export interface PopperScrollGuardOptions {
  insideSelector?: string
  target?: Window | HTMLElement
  capture?: boolean
  useInitGuard?: boolean
}

export interface PopperScrollGuard {
  onShow: () => void
  onHide: () => void
  cleanup: () => void
}

export function createPopperScrollGuard(
  close: () => void,
  options: PopperScrollGuardOptions = {}
): PopperScrollGuard {
  const target = options.target ?? (typeof window !== 'undefined' ? window : undefined)
  const insideSelector = options.insideSelector ?? '.v-popper__popper'
  const capture = options.capture ?? true
  const useInitGuard = options.useInitGuard ?? false

  let isAttached = false
  let isInitializing = false

  const handleScroll = (event: Event): void => {
    if (useInitGuard && isInitializing) return

    const element = event.target as HTMLElement | null
    if (element?.closest?.(insideSelector)) return

    close()
  }

  const onShow = (): void => {
    if (!target || isAttached) return

    if (useInitGuard) {
      isInitializing = true
      nextTick(() => {
        isInitializing = false
      })
    }

    target.addEventListener('scroll', handleScroll, capture)
    isAttached = true
  }

  const onHide = (): void => {
    if (!target || !isAttached) return

    target.removeEventListener('scroll', handleScroll, capture)
    isAttached = false
    isInitializing = false
  }

  const cleanup = (): void => {
    onHide()
  }

  return {
    onShow,
    onHide,
    cleanup
  }
}
